import axios from "axios"
import { withPrefix } from "gatsby"

// utils
import { ApiResponse } from "../../../backend/amplify/utils/api.utils"
import { getBaseUrl } from "../general_utils"

// types
import { EventbriteUserResponse } from "../../components/ProfileMenuBar/ProfileMenuBar"
import {
  SalesforceEBUserAccountResponse,
  AssortmentPlan
} from "../../types/api"
import { UserProfile } from "../../store/slices/user_profile.slice"

export type EventbriteEmailGroup = {
  email: string
  verified: boolean
  primary: boolean
}

export type EventbriteApiUserData = {
  emails: EventbriteEmailGroup[]
  id: string
  name: string
  first_name: string
  last_name: string
  is_public: boolean
  image_id: string
}

export type EventbriteSubscriptionPlanData = {
  status: string
  plan_id: string
  plan_tier: string
  plan_price_id: string
  organization_public_id: string
  created_at: number
  cancel_at_period_end: boolean
  cancels_at: number
  future_phases: any
}

export type EnrichedData = any

export type EventbriteAssortmentResponseData = {
  version: number
  user_id: string
  plan: AssortmentPlan
}

const fetchAssortment = async (
  orgId: string | number
): Promise<EventbriteAssortmentResponseData> => {
  try {
    const assortmentResponse =
      await axios.get<EventbriteAssortmentResponseData>(
        `${getBaseUrl()}/api/v3/organizations/${orgId}/assortment/`,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json"
          }
        }
      )
    const assortmentResponseData = assortmentResponse.data

    return assortmentResponseData
  } catch (error) {
    process.env.GATSBY_DEBUG === "true" &&
      console.warn(
        `fetchAssortment() error message: ${error?.message ?? "Unknown error"}`
      )
  }
}

export type EventbriteOrganization = {
  _type: string
  name: string
  vertical: string
  created: string
  id: string
  image_id?: string | number
  parent_id?: string | number
  locale?: string
}

export type EventbriteOrganizationResponseData = {
  organizations: EventbriteOrganization[]
  pagination: {
    object_count: number
    continuation: string
    page_count: number
    page_size: number
    has_more_items: boolean
    page_number: number
  }
}

const fetchOrganization =
  async (): Promise<EventbriteOrganizationResponseData> => {
    try {
      // fetch user organization
      const organizationResponse =
        await axios.get<EventbriteOrganizationResponseData>(
          `${getBaseUrl()}/api/v3/users/me/organizations/`,
          {
            withCredentials: true,
            headers: {
              "Content-Type": "application/json"
            }
          }
        )

      const organizationData = organizationResponse.data

      return organizationData
    } catch (error) {
      process.env.GATSBY_DEBUG === "true" &&
        console.warn(
          `fetchOrganization() error message: ${
            error?.message ?? "Unknown error"
          }`
        )
    }
  }

export type EventbriteMediaResponseData = {
  edge_color_set: boolean
  edge_color: string
  url: string
  original: {
    url: string
    width: number
    height: number
  }
  crop_mask?: {
    width: number
    height: number
    top_left: {
      y: number
      x: number
    }
  }
  aspect_ratio: string
  id: string
}

export const fetchMedia = async (
  imageId: string,
  height = "32",
  width = "32"
): Promise<EventbriteMediaResponseData | Record<string, never>> => {
  if (!imageId || imageId?.length <= 0) {
    return {}
  }

  try {
    const mediaResponse = await axios.get<EventbriteMediaResponseData>(
      `${getBaseUrl()}/api/v3/media/${imageId}/?width=${width}&height=${height}`,
      {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json"
        }
      }
    )
    const mediaData = mediaResponse.data

    return mediaData
  } catch (error) {
    process.env.GATSBY_DEBUG === "true" &&
      console.warn(
        `fetchMedia() error message: ${error?.message ?? "Unknown error"}`
      )
  }
}

export const fetchDateQualifiedForSupport = async (
  orgId: string
): Promise<SalesforceEBUserAccountResponse> => {
  try {
    const dateQualifiedResponse = await axios.get<
      ApiResponse<{
        data: SalesforceEBUserAccountResponse
      }>
    >(
      withPrefix(
        `/api/salesforce/organization?orgId=${encodeURIComponent(orgId)}`
      ),
      {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json"
        }
      }
    )
    const dateQualifiedData = dateQualifiedResponse.data.result.data

    return dateQualifiedData
  } catch (error) {
    process.env.GATSBY_DEBUG === "true" &&
      console.warn(
        `fetchAssortment() error message: ${error?.message ?? "Unknown error"}`
      )
  }
}

export type EventbriteHtml = {
  text: string
  html: string
}

export type EventbriteTimezone = {
  timezone: string
  utc: string
  local: string
}

export type EvenbriteSubCategoryReference = {
  id: string
  resource_uri: string
  name: string
  parent_category: unknown
}

export type Ticket = {
  currency: string
  value: number
  major_value: string
  display: string
}

export type EventbriteEvent = {
  id: string
  name: EventbriteHtml
  description: EventbriteHtml
  start: EventbriteTimezone
  end: EventbriteTimezone
  url: string
  vanity_url?: string
  created: string
  changed: string
  published: string
  status: string
  currency: string
  online_event: boolean
  organization_id: string
  organizer_id: string
  organizer?: {
    name: string
    description: EventbriteHtml
    long_description: EventbriteHtml
    logo_id: string | number
    logo: EventbriteMediaResponseData
    resource_uri: string
    id: string
    url: string
    num_past_events: number
    num_future_events: number
    twitter: string
    facebook: string
  }
  logo_id: null
  logo: EventbriteMediaResponseData
  venue: {
    name: string
    age_restriction: unknown
    capacity: number
    address: {
      address_1: unknown
      address_2: unknown
      city: unknown
      region: unknown
      postal_code: unknown
      country: unknown
      latitude: unknown
      longitude: unknown
    }
    resource_uri: string
    id: string
    latitude: string
    longitude: string
  }
  format_id: unknown
  format: {
    id: string
    name: string
    name_localized: string
    short_name: string
    short_name_localized: string
    resource_uri: string
  }
  category_id: string
  category: {
    id: string
    resource_uri: string
    name: string
    name_localized: string
    short_name: string
    short_name_localized: string
    subcategories: EvenbriteSubCategoryReference[]
  }
  subcategory_id: string
  subcategory: {
    id: string
    resource_uri: string
    name: string
    parent_category: {
      id: string
      resource_uri: string
      name: string
      name_localized: string
      short_name: string
      short_name_localized: string
      subcategories: unknown
    }
  }
  music_properties: {
    age_restriction: unknown
    presented_by: unknown
    door_time: string
  }
  bookmark_info: {
    bookmarked: boolean
  }
  ticket_availability: {
    has_available_tickets: boolean
    minimum_ticket_price: Ticket
    maximum_ticket_price: Ticket
    is_sold_out: boolean
    start_sales_date: EventbriteTimezone
    waitlist_available: boolean
  }
  listed: boolean
  shareable: boolean
  invite_only: boolean
  show_remaining: boolean
  password: string
  capacity: number
  capacity_is_custom: boolean
  tx_time_limit: string
  hide_start_date: boolean
  hide_end_date: boolean
  locale: string
  is_locked: boolean
  privacy_setting: string
  is_externally_ticketed: boolean
  external_ticketing: {
    external_url: string
    ticketing_provider_name: string
    is_free: boolean
    minimum_ticket_price: Ticket
    maximum_ticket_price: Ticket
    sales_start: string
    sales_end: string
  }
  is_series: boolean
  is_series_parent: boolean
  series_id: string
  is_reserved_seating: boolean
  show_pick_a_seat: boolean
  show_seatmap_thumbnail: boolean
  show_colors_in_seatmap_thumbnail: boolean
  is_free: boolean
  source: string
  version: string
  resource_uri: string
  event_sales_status: {
    sales_status: string
    start_sales_date: EventbriteTimezone
  }
  checkout_settings: {
    created: string
    changed: string
    country_code: string
    currency_code: string
    checkout_method: string
    offline_settings: [
      {
        payment_method: string
        instructions: string
      }
    ]
    user_instrument_vault_id: string
  }
}

export type EventbriteEventsResponse = {
  pagination: {
    object_count: number
    page_number: number
    page_size: number
    page_count: number
    continuation: string
    has_more_items: boolean
  }
  events: EventbriteEvent[]
}

export type EventbriteInsightInterval = {
  organization_id: string
  total_net_sales: number
  total_tickets_sold: number
  total_orders: number
  eb_driven_tickets_sold: number
}

export type EventbriteOrgInsightsResponse = {
  days_360: EventbriteInsightInterval
  days_180?: EventbriteInsightInterval
  days_90?: EventbriteInsightInterval
  days_7?: EventbriteInsightInterval
}

export const fetchEvents = async (
  orgId: string
): Promise<EventbriteEventsResponse> => {
  try {
    const userEventsResponse = await axios.get<EventbriteEventsResponse>(
      `${getBaseUrl()}/api/v3/organizations/${orgId}/events/?time_filter=current_future`,
      {
        withCredentials: true
      }
    )
    const userEventsData = userEventsResponse.data

    return userEventsData
  } catch (error) {
    process.env.GATSBY_DEBUG === "true" &&
      console.warn(
        `fetchEvents() error message: ${error?.message ?? "Unknown error"}`
      )
  }
}

export const fetchSalesHistory = async (
  orgId: string
): Promise<EventbriteOrgInsightsResponse> => {
  try {
    const salesHistory = await axios.get<EventbriteOrgInsightsResponse>(
      `${getBaseUrl()}/api/v3/creator_home/organizations/${orgId}/insights/`,
      {
        withCredentials: true
      }
    )

    const salesHistoryData = salesHistory?.data
    return salesHistoryData
  } catch (error) {
    process.env.GATSBY_DEBUG === "true" &&
      console.warn(
        `fetchSalesHistory() error message: ${
          error?.message ?? "Unknown error"
        }`
      )
  }
}

export const fetchSubscription = async (
  orgId: string
): Promise<EventbriteSubscriptionPlanData> => {
  try {
    const userSubscriptionResponse =
      await axios.get<EventbriteSubscriptionPlanData>(
        `${getBaseUrl()}/api/v3/organizations/${orgId}/subscription/`,
        {
          withCredentials: true
        }
      )
    const userSubscriptionData = userSubscriptionResponse.data

    return userSubscriptionData
  } catch (error) {
    process.env.GATSBY_DEBUG === "true" &&
      console.warn(
        `fetchSubscription() error message: ${
          error?.message ?? "Unknown error"
        }`
      )
  }
}

export type EnrichedUserData = {
  userAuthData: EventbriteUserResponse
  mediaData: EventbriteMediaResponseData | Record<string, never>
  orgData: EventbriteOrganizationResponseData
  assortmentResponseData: EventbriteAssortmentResponseData
  qualifiedForSupportData: SalesforceEBUserAccountResponse
  userEventsData: EventbriteEventsResponse
  salesHistoryData: EventbriteOrgInsightsResponse
  organizationId: string
  userOrderData: EventbriteOrderResponse
  subscriptionData: EventbriteSubscriptionPlanData
}

type SingleLogo = {
  crop_mask: {
    top_left: {
      x: number
      y: number
    }
    width: number
    height: number
  }
  original: {
    url: string
    width: number
    height: number
  }
  id: string
  url: string
  aspect_ratio: string
  edge_color: string
  edge_color_set: boolean
}

export type SingleEvent = {
  name: {
    text: string
    html: string
  }
  description: {
    text: string
    html: string
  }
  start: {
    timezone: string
    local: string
    utc: string
  }
  status: string
  online_event: boolean
  logo?: SingleLogo
  refund_policy?: {
    refund_policy: string
    refund_retention_policy: any
    is_attendee_automated_refund_allowed: boolean
    default_refund_retention_policy_strategy: any
    refund_methods: string[]
    credit_expiration_period: any
    is_refund_request_allowed: any
    validity_days: any
    refund_policy_description: any
  }
  // there are a lot of other properties of a full Event that are not fully typed here yet
  [key: string]: any
}

export type EventbriteOrder = {
  costs: {
    base_price: {
      display: string
      currency: string
      value: number
      major_value: string
    }
    eventbrite_fee: {
      display: string
      currency: string
      value: number
      major_value: string
    }
    gross: {
      display: string
      currency: string
      value: number
      major_value: string
    }
    payment_fee: {
      display: string
      currency: string
      value: number
      major_value: string
    }
    tax: {
      display: string
      currency: string
      value: number
      major_value: string
    }
  }
  resource_uri: string
  id: string
  changed: string
  created: string
  name: string
  first_name: string
  last_name: string
  email: string
  status: string
  time_remaining: null
  event_id: string
  event?: SingleEvent
}

export type EventbriteOrderResponse = {
  pagination: {
    object_count: number
    page_number: number
    page_size: number
    page_count: number
    has_more_items: boolean
  }
  orders: EventbriteOrder[]
}

export const fetchOrders = async (): Promise<EventbriteOrderResponse> => {
  try {
    const userOrdersData = await axios.get<EventbriteOrderResponse>(
      `${getBaseUrl()}/api/v3/users/me/orders/?time_filter=current_future&expand=event,event.logo,event.refund_policy`,
      {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json"
        }
      }
    )

    return userOrdersData?.data
  } catch (error: any) {
    process.env.GATSBY_DEBUG === "true" &&
      console.warn(
        `fetchOrders() error message: ${error?.message ?? "Unknown error"}`
      )

    // @ts-ignore
    return { orders: [], pagination: {} }
  }
}

export const enrichUserData = async (
  userResponse: EventbriteUserResponse,
  selectedOrgId = ""
): Promise<UserProfile> => {
  if (typeof window !== "undefined") {
    const controller = new AbortController()
    const timeoutController = setTimeout(() => {
      controller.abort()
    }, 30000)

    try {
      // fetch user image if image_id exists
      const mediaData = await fetchMedia(userResponse.image_id)
      const orgData = await fetchOrganization()

      type AuthValidateResponse = {
        uid: number
        oid: string | "uid"
        valid: boolean
        email: string
        language: string
      }

      let orgId = ""
      if (selectedOrgId) {
        orgId = selectedOrgId
      } else {
        try {
          const authValidateRequest = await axios.get<AuthValidateResponse>(
            `${getBaseUrl()}/auth/validate/`,
            {
              withCredentials: true,
              headers: {
                "Content-Type": "application/json"
              }
            }
          )
          const authValidateData: AuthValidateResponse =
            authValidateRequest?.data

          // @TODO: add a modal to select the org the user wants to use during the login process.
          // the 'oid' value can return as a number string OR as 'uid'. Meaning the user has not signed into an organization from product. When that happens we have to just choose the first org available in their list of orgs.
          if (
            authValidateData?.oid === "uid" ||
            authValidateData?.oid?.toLowerCase() === "none"
          ) {
            orgId = orgData?.organizations[0]?.id
          } else {
            orgId = authValidateData?.oid
          }
        } catch (authValidateError) {
          if (process.env.GATSBY_DEBUG === "true") {
            console.warn("Enrichment error: ", {
              authValidateError
            })
          }

          orgId = orgData?.organizations[0]?.id
        }
      }

      const assortmentResponseData = await fetchAssortment(orgId)
      // fetch user organization
      const qualifiedForSupportData = await fetchDateQualifiedForSupport(orgId)
      const userEventsData = await fetchEvents(orgId)
      const salesHistoryData = await fetchSalesHistory(orgId)
      const userOrderData = await fetchOrders()
      const subscriptionData = await fetchSubscription(orgId)

      const compiledData: EnrichedUserData = {
        userAuthData: userResponse,
        mediaData,
        orgData,
        assortmentResponseData,
        qualifiedForSupportData,
        userEventsData,
        salesHistoryData,
        userOrderData,
        organizationId: orgId,
        subscriptionData
      }

      return composeUserData(compiledData)
    } catch (error) {
      const errorMessage =
        error?.name === "AbortError"
          ? "Request timed out. Please check your connection and try again."
          : error?.message?.length > 0
          ? error?.message
          : "There was an error authorizing request."

      console.warn("Enrichment error: ", { error, errorMessage })
    } finally {
      clearTimeout(timeoutController)
    }
  }
}

const defineIsFree = (
  userEventsData: EventbriteEventsResponse,
  salesHistoryData: EventbriteOrgInsightsResponse,
  assortmentPlan: AssortmentPlan,
  subscription: EventbriteSubscriptionPlanData
) => {
  // If a user is package3, or subscribed to a pro plan, they should be considered paid even if their events are free
  if (
    assortmentPlan === "package3" ||
    subscription?.status?.toLowerCase() === "active"
  ) {
    return false
  }

  if (salesHistoryData?.days_360?.total_net_sales > 0) {
    return false
  }

  if (!userEventsData?.events || userEventsData?.events?.length <= 0) {
    return true
  }

  return !userEventsData?.events?.some((singleEvent) => {
    if (singleEvent.is_free === false) {
      return true
    }
    return false
  })
}

export const retrieveOrganization = (
  orgData: EventbriteOrganizationResponseData,
  orgId: string
): EventbriteOrganization => {
  return orgData?.organizations?.find((org) => org?.id === orgId)
}

export const composeUserData = (data: EnrichedUserData): UserProfile => {
  // retrieve the currently selected organization
  const selectedOrg = retrieveOrganization(data?.orgData, data?.organizationId)

  const orgCreatedDate = new Date(data?.orgData?.organizations[0]?.created)
  // to determine if an account is considered new we need to check the 'created' date of the organization is less than 90 days from current
  const currentDate = new Date()
  const differenceInTime = currentDate.getTime() - orgCreatedDate.getTime()
  const differenceInDays = differenceInTime / (1000 * 3600 * 24)

  // if the user has any events then we need to loop through all events to see if any of them are paid events aka is_free=false
  // if you are an attendee then this is always false
  const isPaidCreator = !defineIsFree(
    data.userEventsData,
    data?.salesHistoryData,
    data?.assortmentResponseData?.plan,
    data?.subscriptionData
  )

  // @TODO: confirm how many days an account is considered new
  const isNewAccount = differenceInDays <= 90

  // If the Date_Qualified_For_Support__c field has a value, creator is High Value
  const isHighValue = data?.qualifiedForSupportData
    ?.Date_Qualified_For_Support__c
    ? true
    : false

  // If the SalesPackage__c field has a value, creator is a Sales Creator
  const isSalesCreator = data?.qualifiedForSupportData?.SalesPackage__c
    ? true
    : false

  // check if a user is an organizer by looking if any events have been created by the user/organization
  const hasUpcomingEvents =
    data?.userEventsData?.events?.length > 0

  // for a user to be an attendee all of these values need to be false
  const isAttendee =
    !isPaidCreator &&
    !isHighValue &&
    !isSalesCreator &&
    !hasUpcomingEvents

  return {
    allData: data,
    assortmentPlan: data?.assortmentResponseData?.plan,
    email: data?.userAuthData?.emails[0]?.email,
    organizationId: data?.organizationId,
    userData: data?.userAuthData,
    avatar: data?.mediaData,
    name: selectedOrg?.name,
    isAttendee,
    isOrganizer: hasUpcomingEvents,
    // isFree and isFreeCreator are identical, keeping isFree for now until we can test that Salesforce endpoints don't need that data
    isFree: !isPaidCreator && hasUpcomingEvents,
    isFreeCreator: !isPaidCreator && hasUpcomingEvents,
    isPaidCreator: isPaidCreator,
    isSalesCreator,
    isHighValue,
    isNewAccount,
    orders: data?.userOrderData?.orders,
    events: data?.userEventsData?.events
  }
}
